import { useEffect, type RefObject } from 'react';

/**
 *
 * @param event - The event to check if it is a WheelEvent
 * @returns True if the event is a WheelEvent, false otherwise
 */

const isWheelEvent = (event: Event): event is WheelEvent =>
  'metaKey' in event || 'ctrlKey' in event;

/**
 * A custom React hook that prevents the default scrolling behavior of the page
 * when the Command (Mac) or Control (Windows) key is pressed while using the mouse wheel.
 *
 * @param targetRef - An optional React ref object pointing to a target HTMLDivElement
 * or SVGRectElement. If not provided, the event listener will be attached to the
 * document.
 */
export function usePreventScrollOnWheel(
  targetRef?: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    const handleWheel = (event: Event) => {
      if (isWheelEvent(event) && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
      }
    };

    const targetRefCurrent = targetRef?.current ?? document;

    targetRefCurrent.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    return () => {
      targetRefCurrent.removeEventListener('wheel', handleWheel);
    };
  }, [targetRef]);
}
